import { Button, Flex, Text, Textarea } from "@chakra-ui/react";
import { Field, FieldProps, Form, Formik } from "formik";
import React from "react";
import { RiEditLine } from "react-icons/ri";
import { CustomFormInput } from "src/components/Inputs/CustomFormInput";
import { Card } from "src/components/Layout/Card";
import { useFormTemplateContext } from "src/scenes/orgAdmin/formTemplates/formTemplateContext";
import { validateWithZod } from "src/services/formValidations";
import * as AF from "src/types/formTemplate";
import { z } from "zod";
import { DirtyFormTracker } from "../QuestionForm/DirtyFormTracker";

type Mode = "View" | "Edit";
type Props = {
  section: AF.V2.DisclaimerSection;
  onUpdate?: (disclaimer: AF.V2.DisclaimerSection) => void;
};
export const DisclaimerForm: React.FC<Props> = ({ section, onUpdate }) => {
  const [mode, setMode] = React.useState<Mode>("View");
  const onEditButtonClick = React.useCallback(() => {
    setMode("Edit");
  }, []);
  const onCancelButtonClick = React.useCallback(() => {
    setMode("View");
  }, []);

  const onUpdateButtonClick = React.useCallback(
    (updatedDisclaimer: AF.V2.DisclaimerSection) => {
      if (onUpdate) onUpdate(updatedDisclaimer);
      setMode("View");
    },
    [onUpdate]
  );

  const children = React.useMemo(() => {
    switch (mode) {
      case "Edit":
        return (
          <DisclaimerFormEdit
            disclaimer={section}
            onCancelButtonClick={onCancelButtonClick}
            onUpdate={onUpdateButtonClick}
          />
        );

      case "View":
        return (
          <DisclaimerFormView
            disclaimer={section}
            onEditButtonClick={onEditButtonClick}
          />
        );

      default:
        const _exhaustiveCheck: never = mode;
        return _exhaustiveCheck;
    }
  }, [
    mode,
    section,
    onCancelButtonClick,
    onUpdateButtonClick,
    onEditButtonClick,
  ]);

  return (
    <Card display="flex" flexDirection="column" showBorder gap="3">
      {children}
    </Card>
  );
};

type ViewProps = {
  disclaimer: AF.V2.DisclaimerSection;
  onEditButtonClick: () => void;
};
export const DisclaimerFormView: React.FC<ViewProps> = ({
  disclaimer,
  onEditButtonClick,
}) => {
  const { mode } = useFormTemplateContext();
  return (
    <Flex direction="column" gap="3">
      <Flex justifyContent="flex-end" alignItems="center">
        {mode === "edit" ? (
          <Button
            colorScheme="gray"
            variant="outline"
            leftIcon={<RiEditLine />}
            flexShrink={0}
            onClick={onEditButtonClick}
          >
            Edit disclaimer
          </Button>
        ) : null}
      </Flex>
      <Text fontWeight="bold">Disclaimer Text</Text>
      <Text fontSize="sm" whiteSpace="pre-wrap">
        {disclaimer.disclaimer}
      </Text>
    </Flex>
  );
};

const FormSchema = z.object({
  disclaimer: z.string().min(1, "This field is required"),
});
type FormType = z.infer<typeof FormSchema>;
type EditProps = {
  disclaimer: AF.V2.DisclaimerSection;
  onCancelButtonClick: () => void;
  onUpdate: (question: AF.V2.DisclaimerSection) => void;
};
export const DisclaimerFormEdit: React.FC<EditProps> = ({
  disclaimer,
  onCancelButtonClick,
  onUpdate,
}) => {
  const initialValues = {
    disclaimer: disclaimer.disclaimer,
  };
  const onSubmit = React.useCallback(
    (values: FormType) => {
      onUpdate({ ...disclaimer, disclaimer: values.disclaimer });
    },
    [disclaimer, onUpdate]
  );
  return (
    <Formik<FormType>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={validateWithZod(FormSchema)}
    >
      {(formik) => (
        <Flex as={Form} direction="column" gap="3">
          <DirtyFormTracker />
          <Field name="disclaimer">
            {({ field, meta }: FieldProps) => (
              <CustomFormInput label="Disclaimer Text" error={meta.error}>
                <Textarea height="20rem" {...field} />
              </CustomFormInput>
            )}
          </Field>
          <Flex gap="3" justifyContent="right" paddingY="3">
            <Button
              variant="ghost"
              colorScheme="gray"
              onClick={onCancelButtonClick}
            >
              Cancel
            </Button>
            <Button type="submit">Update disclaimer</Button>
          </Flex>
        </Flex>
      )}
    </Formik>
  );
};
